<script setup>
import { ref } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

const props = defineProps({
  agentConfig: {
    type: Object,
    required: true,
  },
  publishedJson: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['publish']);

const copyJson = async () => {
  try {
    await copyTextToClipboard(props.publishedJson);
    useAlert('JSON copiado para a área de transferência!');
  } catch (error) {
    useAlert('Falha ao copiar JSON.');
  }
};
</script>

<template>
  <div class="space-y-6 max-w-4xl">
    <div class="bg-n-surface-1 border border-n-weak rounded-xl p-6 space-y-4">
      <div class="flex items-center justify-between">
        <div>
          <h3 class="text-base font-semibold text-n-slate-12">
            Status de Publicação & Versionamento
          </h3>
          <p class="text-xs text-n-slate-11">
            Ao publicar, esta versão se torna a versão ativa consumida pelo n8n
            via API.
          </p>
        </div>

        <Button
          emerald
          icon="i-lucide-upload-cloud"
          label="Publicar Versão Agora"
          @click="emit('publish')"
        />
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
        <div class="bg-n-surface-2 border border-n-weak rounded-xl p-4">
          <span class="text-xs font-semibold text-n-slate-11 block mb-1">Status Atual</span>
          <span
            class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold"
            :class="
              agentConfig.status === 'published'
                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-400 border border-emerald-500/20'
                : 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-400 border border-amber-500/20'
            "
          >
            <span
              class="size-1.5 rounded-full mr-1.5"
              :class="
                agentConfig.status === 'published'
                  ? 'bg-emerald-500'
                  : 'bg-amber-500'
              "
            />
            {{
              agentConfig.status === 'published'
                ? 'Publicado (Ativo)'
                : 'Rascunho (Draft)'
            }}
          </span>
        </div>

        <div class="bg-n-surface-2 border border-n-weak rounded-xl p-4">
          <span class="text-xs font-semibold text-n-slate-11 block mb-1">Versão Ativa</span>
          <span class="font-mono text-base font-bold text-n-slate-12">
            v{{ agentConfig.version || 1 }}
          </span>
        </div>

        <div class="bg-n-surface-2 border border-n-weak rounded-xl p-4">
          <span class="text-xs font-semibold text-n-slate-11 block mb-1">Última Publicação</span>
          <span class="text-xs text-n-slate-12 font-medium">
            {{
              agentConfig.published_at
                ? new Date(agentConfig.published_at).toLocaleString()
                : 'Ainda não publicado'
            }}
          </span>
        </div>
      </div>

      <div v-if="publishedJson" class="pt-4 space-y-2">
        <div class="flex items-center justify-between">
          <span
            class="text-xs font-bold text-n-slate-12 uppercase flex items-center gap-1.5"
          >
            <Icon icon="i-lucide-code" class="size-4 text-n-brand" />
            JSON Consolidado para o n8n (/published)
          </span>
          <Button
            ghost
            sm
            icon="i-lucide-copy"
            label="Copiar JSON"
            @click="copyJson"
          />
        </div>

        <div
          class="p-4 rounded-xl bg-slate-950 font-mono text-xs text-emerald-400 max-h-96 overflow-auto border border-n-weak"
        >
          <pre>{{ publishedJson }}</pre>
        </div>
      </div>
    </div>
  </div>
</template>
